import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { t } from '@lingui/core/macro';
import { invalidateQuery, noddiAsync, URLKeys } from 'noddi-async';
import { CarSpec } from 'noddi-types';
import { NoddiButton, NoddiCard, NoddiSheet } from 'noddi-ui-web';
import { getCarDisplayName } from 'noddi-util';
import { RegNumberSearch } from 'noddi-web';
import { useState } from 'react';
import { useCurrentBrand } from '../../../stores/BrandStore/brandStore';
import { getCommonTranslations } from '../../../utils/commonTranslations';
import { getErrorCodesWithTrans } from '../../../utils/getErrorCodesWithTranslations';

type FoundCar = CarSpec & { id: number };

type Props = {
  userGroupId: number;
};

export const AddFleetCarSheet = ({ userGroupId }: Props) => {
  const { currentBrand, brandEmail } = useCurrentBrand();
  const [open, setOpen] = useState(false);
  const [foundCar, setFoundCar] = useState<FoundCar | null>(null);

  const {
    mutate: addCarToFleet,
    isPending,
    error,
    reset
  } = noddiAsync.usePost({
    type: URLKeys.postAddCarToUserGroup,
    queryConfig: {
      // The list must include the new car before the sheet closes.
      onSuccess: () => invalidateQuery({ urlKey: URLKeys.getPaginatedFleetViewCars })
    }
  });

  const close = () => {
    setOpen(false);
    setFoundCar(null);
    reset();
  };

  return (
    <>
      <NoddiButton variant='outline' size='small' startIcon='Plus' onClick={() => setOpen(true)}>
        {t`Add car`}
      </NoddiButton>
      <NoddiSheet
        open={open}
        onOpenChange={(next) => {
          if (next) {
            setOpen(true);
            return;
          }
          close();
        }}
        title={t`Add car`}
        description={t`Search by license plate to add a car to your fleet.`}
        footer={
          <NoddiButton
            className='w-full'
            endIcon='Plus'
            disabled={!foundCar}
            loading={isPending}
            onClick={() => {
              if (!foundCar) {
                return;
              }
              addCarToFleet(
                { userGroupId, carIds: [foundCar.id] },
                {
                  onSuccess: close
                }
              );
            }}
          >
            {t`Add car`}
          </NoddiButton>
        }
      >
        <div className='flex flex-col gap-4'>
          <RegNumberSearch
            addCar={(car) => {
              reset();
              setFoundCar(car);
            }}
            translations={{
              ...getCommonTranslations().getRegNumberText(),
              add: t`Search`
            }}
            errorCodes={getErrorCodesWithTrans(brandEmail)}
            brandDomain={currentBrand}
          />
          {foundCar && (
            <div className='flex flex-col gap-2'>
              <p className='text-sm font-semibold'>{t`Found car`}</p>
              <NoddiCard>
                <p>{getCarDisplayName({ car: foundCar })}</p>
              </NoddiCard>
            </div>
          )}
          {error && <ApiErrorMessageWithTrans error={error} />}
        </div>
      </NoddiSheet>
    </>
  );
};
